import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NetworkStatusService } from './network-status.service';
import { HubDeviceService } from './hub-device.service';
import { StoreRealtimeService } from '../services/store-realtime.service';
import { ReconnectPolicyService } from './reconnect-policy.service';

/**
 * RECON-6 — follow the hub back online.
 *
 * A store runs one hub and several satellites. Before this, each terminal
 * decided for itself from its own heartbeat, which meant the floor could
 * split: some terminals on the cloud, some still on the hub, at the exact
 * moment the hub was mid-handover. The hub is the only device that knows
 * whether the cloud actually has the service's writes, so it decides and
 * the rest follow.
 *
 * Following is not obedience. On the hub's all-clear a satellite:
 *
 *   1. probes the cloud ITSELF — the hub's reachability does not prove a
 *      satellite's (different SSID or VLAN, a captive portal that dropped
 *      its session), and in local-origin deployments a terminal that
 *      followed blindly onto an unreachable cloud would have no app left;
 *   2. refuses to switch out from under an operator who is mid-order, and
 *      retries when they come back to the floor plan.
 *
 * The hub device itself is excluded — it is the one doing the announcing.
 */
@Injectable({ providedIn: 'root' })
export class SatelliteFollowService {
  private net = inject(NetworkStatusService);
  private hubDevice = inject(HubDeviceService);
  private realtime = inject(StoreRealtimeService);
  private router = inject(Router);
  private policy = inject(ReconnectPolicyService);

  /** Set while the idle guard is counting down. */
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * True once the guard has elapsed for the CURRENT all-clear.
   *
   * Without this the guard re-arms forever: the timer callback nulls
   * `idleTimer` before re-entering `tryFollow`, which then sees "no timer
   * running" and schedules another one. Reset on each new announcement so
   * a later outage waits again.
   */
  private idleGuardElapsed = false;

  /** Set when an all-clear arrived while the operator was mid-order. */
  private pendingFollow = false;

  /**
   * Screens a switch must not happen underneath. Everything else — floor
   * plan, session orders, reports — can change transport between two taps
   * without the operator noticing, because nothing is half-entered.
   *
   * Deliberately route-based rather than a modal/dirty-state check: it is
   * the coarse, reliable signal. A false "busy" costs one deferral; a
   * false "idle" costs a half-typed payment.
   */
  // PTM-6 — '/pos/pt/settle' is the Payment Terminal's settle screen: a
  // transport switch under a card sale is exactly the "half-typed payment"
  // case above. (Payterm is online-only — D7 — so this is belt-and-braces.)
  private readonly BUSY_ROUTES = ['/pos/order', '/pos/settle', '/pos/pt/settle'];

  start(): void {
    this.realtime.hubGoingOnline$.subscribe((msg) => {
      if (this.hubDevice.isHubDevice()) return;   // the announcer
      // RECON-7 — the store can turn following off entirely (Manual, or
      // an explicit opt-out) and switch each terminal by hand.
      if (!this.policy.autoFollowSatellites()) {
        // eslint-disable-next-line no-console
        console.info('[satellite] hub all-clear ignored — store policy is', this.policy.mode());
        return;
      }
      // eslint-disable-next-line no-console
      console.info('[satellite] hub all-clear', msg);
      // A new announcement is a new outage — wait again.
      this.idleGuardElapsed = false;
      if (this.idleTimer !== null) { clearTimeout(this.idleTimer); this.idleTimer = null; }
      void this.tryFollow();
    });

    // Retry when the operator lands somewhere it is safe to switch.
    this.router.events.subscribe(() => {
      if (this.pendingFollow && !this.isBusy()) void this.tryFollow();
    });
  }

  private isBusy(): boolean {
    const url = (this.router.url ?? '').split('?')[0];
    return this.BUSY_ROUTES.some(r => url.startsWith(r));
  }

  private async tryFollow(): Promise<void> {
    if (this.net.current() === 'online') { this.pendingFollow = false; return; }
    if (!this.policy.autoFollowSatellites()) { this.pendingFollow = false; return; }

    if (this.isBusy()) {
      this.pendingFollow = true;
      // eslint-disable-next-line no-console
      console.info('[satellite] deferring switch — operator is mid-order');
      return;
    }

    // RECON-7 — hold for the store's idle guard before switching, so a
    // terminal that has JUST been put down does not change transport
    // under the next person to pick it up. Zero disables the wait.
    const guardMs = this.policy.idleGuardSeconds() * 1000;
    if (guardMs > 0 && !this.idleGuardElapsed && this.idleTimer === null) {
      this.pendingFollow = true;
      // eslint-disable-next-line no-console
      console.info(`[satellite] waiting ${guardMs / 1000}s of idle before switching`);
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        this.idleGuardElapsed = true;
        // Re-check rather than switching blind: the operator may have
        // started an order during the wait, in which case `tryFollow`
        // defers again exactly as it would have at announcement time.
        void this.tryFollow();
      }, guardMs);
      return;
    }
    const switched = await this.net.acceptCorroboratedRecovery();
    // Keep the intent if our own probe disagreed with the hub: the WAN may
    // reach this terminal a moment later, and the hub will not announce
    // twice.
    this.pendingFollow = !switched;
  }
}
